import React, { useState } from 'react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { CheckCircle, XCircle, MessageSquare, Loader2 } from 'lucide-react';

/**
 * Modal reutilizable para validación de solicitudes (Capacitaciones/Actividades)
 * Solo visible para usuarios con permisos de Comandante
 * 
 * @param {boolean} open - Estado de apertura del modal
 * @param {function} onOpenChange - Callback para cambiar estado de apertura
 * @param {object} item - Item a validar (capacitación o actividad)
 * @param {string} itemType - Tipo de item ('capacitacion' o 'actividad')
 * @param {function} onValidar - Callback async para ejecutar la validación
 * @param {function} onSuccess - Callback cuando la validación es exitosa
 */
export function ModalValidacion({ 
    open, 
    onOpenChange, 
    item, 
    itemType = 'actividad',
    onValidar,
    onSuccess 
}) {
    const [observacion, setObservacion] = useState('');
    const [loading, setLoading] = useState(false);

    const handleValidar = async (estado) => {
        if (estado === 'rechazado' && !observacion.trim()) {
            toast.error('Debe ingresar una observación para rechazar');
            return;
        }

        setLoading(true);
        try {
            await onValidar({
                estado,
                observacion_comandante: observacion.trim() || null
            });
            
            toast.success(
                estado === 'autorizado' 
                    ? `${itemType === 'capacitacion' ? 'Capacitación' : 'Actividad'} autorizada correctamente`
                    : `${itemType === 'capacitacion' ? 'Capacitación' : 'Actividad'} rechazada`
            );
            
            setObservacion('');
            onOpenChange(false);
            onSuccess?.();
        } catch (err) {
            console.error('Error validando:', err);
            toast.error(err.response?.data?.detail || 'Error al validar');
        } finally {
            setLoading(false);
        }
    };

    const getTitle = () => {
        return itemType === 'capacitacion' 
            ? 'Validar Capacitación' 
            : 'Validar Actividad Extra Programática';
    };

    const getItemName = () => {
        if (!item) return '';
        return itemType === 'capacitacion' 
            ? item.nombre 
            : item.actividad;
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <MessageSquare className="h-5 w-5 text-primary" />
                        {getTitle()}
                    </DialogTitle>
                    <DialogDescription>
                        Revise la solicitud y autorice o rechace según corresponda.
                    </DialogDescription>
                </DialogHeader>

                {item && (
                    <div className="space-y-4 py-4">
                        {/* Info del item */}
                        <div className="bg-muted/50 p-4 rounded-lg space-y-2">
                            <div className="flex items-center justify-between">
                                <span className="text-sm font-medium">
                                    {itemType === 'capacitacion' ? 'Capacitación' : 'Actividad'}:
                                </span>
                                <Badge variant="outline">
                                    {item.estado_validacion || item.estado || 'pendiente'}
                                </Badge>
                            </div>
                            <p className="text-sm">{getItemName()}</p>
                            
                            {item.fecha && (
                                <div className="text-xs text-muted-foreground">
                                    Fecha: {new Date(item.fecha || item.fecha_inicio).toLocaleDateString('es-CL')}
                                </div>
                            )}
                            
                            {item.compania_nombre && (
                                <div className="text-xs text-muted-foreground">
                                    Compañía: {item.compania_nombre}
                                </div>
                            )}
                            
                            {item.solicitante_nombre && (
                                <div className="text-xs text-muted-foreground">
                                    Solicitante: {item.solicitante_nombre}
                                </div>
                            )}
                        </div>

                        {/* Campo de observaciones */}
                        <div className="space-y-2">
                            <Label htmlFor="observacion">
                                Observaciones del Comandante
                            </Label>
                            <Textarea
                                id="observacion"
                                placeholder="Ingrese observaciones (obligatorio para rechazar)..."
                                value={observacion}
                                onChange={(e) => setObservacion(e.target.value)}
                                rows={3}
                                data-testid="input-observacion-comandante"
                            />
                        </div>
                    </div>
                )}

                <DialogFooter className="flex gap-2 sm:gap-2">
                    <Button 
                        variant="outline" 
                        onClick={() => onOpenChange(false)}
                        disabled={loading}
                    >
                        Cancelar
                    </Button>
                    <Button
                        variant="destructive"
                        onClick={() => handleValidar('rechazado')}
                        disabled={loading}
                        data-testid="btn-rechazar"
                    >
                        {loading ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            <>
                                <XCircle className="h-4 w-4 mr-2" />
                                Rechazar
                            </>
                        )}
                    </Button>
                    <Button
                        onClick={() => handleValidar('autorizado')}
                        disabled={loading}
                        className="bg-green-600 hover:bg-green-700"
                        data-testid="btn-autorizar"
                    >
                        {loading ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            <>
                                <CheckCircle className="h-4 w-4 mr-2" />
                                Autorizar
                            </>
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default ModalValidacion;
